import { AxiosResponse } from 'axios';
import environment from '@environment';
import { CacheAreaKey } from 'core/config/cache-config';
import { ProfileCountry, ProfileLocation, ProfilePreferences, ProfileUser } from 'core/models/account/profile';
import { EndpointHelper } from 'core/services/endpoint.helper';
import { HttpClient } from 'core/services/http';
import { StorageHelper } from 'core/services/storage';
import { SiteConfigStore } from 'core/stores/site-config.store';

export type ResponseFromMAHGetLocations = {
  id: number,
  name: string,
  cityList: {
    id: number,
    name: string,
    suburbList: {
      id: number,
      name: string,
      postalCode: string
    }[]
  }[]
}[]

export type ResponseFromZAGetLocations = {
  provinces: {
    name: string,
    cities: {
      name: string,
      suburbs: {
        name: string,
        postalCodes: string[]
      }[]
    }[]
  }[]
}

export class LocationHelper {
  // FIXME: this should be resolve on the API - remove once cleaned-up there.
  static MapToLocations(locations: ResponseFromMAHGetLocations | ResponseFromZAGetLocations): ProfileLocation {
    if (locations && Object.keys(locations)?.length && Object.keys(locations).includes('provinces')) {
      return locations as unknown as ProfileLocation;
    }

    const mahLocations = locations as unknown as ResponseFromMAHGetLocations;

    return mahLocations.reduce((prev: ResponseFromZAGetLocations, curr) => ({
      provinces: [
        ...prev.provinces,
        {
          name: curr.name,
          cities: curr.cityList.map(city => ({
            name: city.name,
            suburbs: city.suburbList.map(suburb => ({
              name: suburb.name,
              postalCodes: [suburb.postalCode]
            }))
          }))
        }
      ]
    }) as ResponseFromZAGetLocations, { provinces: [] });
  }
}

export class ProfileService {
  private static apiBuilder = new EndpointHelper(`${environment.MAH_API_BASE_URL}/${environment.MAH_API_CUSTOMER_PROFILE}/${environment.MAH_API_VERSION}`);
  private static menuApiBuilder = new EndpointHelper(`${environment.MAH_API_BASE_URL}/menu/`)

  static getProfile(customerNumber: string, country: string): Promise<ProfileUser> {
    const url = this.apiBuilder.createUrl('UpdateContactDetails')
      .addRouteParam('GetCustomerProfile')
      .addQueryParam('Country', country)
      .addQueryParam('CustomerNumber', customerNumber)
      .addQueryParam('BusinessUnit', SiteConfigStore.businessUnit())
      .addQueryParam('Channel', SiteConfigStore.channel())
      .build();

    const storedItems = (StorageHelper<ProfileUser>).GetInstance.getFromCache('req', url, CacheAreaKey.Profile);

    if (storedItems) {
      return Promise.resolve(storedItems as ProfileUser);
    };

    return HttpClient.GetInstance.get<ProfileUser>(url)
      .then(
        (res: AxiosResponse<ProfileUser>) => (
          (StorageHelper<ProfileUser>).GetInstance.setCacheAndReturn('req', CacheAreaKey.Profile, res?.data, url) as ProfileUser
        )
      );
  }

  static getProfileImage(customerNumber: string, country: string): Promise<{ image: string }> {
    const url = this.apiBuilder.createUrl('Image')
      .addRouteParam('GetProfilePicture')
      .addQueryParam('Country', country)
      .addQueryParam('CustomerNumber', customerNumber)
      .addQueryParam('BusinessUnit', SiteConfigStore.businessUnit())
      .addQueryParam('Channel', SiteConfigStore.channel())
      .build();

    const storedItems = (StorageHelper<{ image: string }>).GetInstance.getFromCache('req', url, CacheAreaKey.Profile);

    if (storedItems) {
      return Promise.resolve(storedItems as { image: string });
    };

    return HttpClient.GetInstance.get<{ image: string }>(url)
      .then(
        (res: AxiosResponse<{ image: string }>) => (
          (StorageHelper<{ image: string }>).GetInstance.setCacheAndReturn('req', CacheAreaKey.Profile, res?.data, url) as { image: string }
        )
      );
  }

  static getPreferences(customerNumber: string, country: string): Promise<ProfilePreferences> {
    const url = this.apiBuilder.createUrl('Preferences')
      .addRouteParam('Get')
      .addQueryParam('Country', country)
      .addQueryParam('CustomerNumber', customerNumber)
      .addQueryParam('BusinessUnit', SiteConfigStore.businessUnit())
      .addQueryParam('Channel', SiteConfigStore.channel())
      .build();

    const storedItems = (StorageHelper<any>).GetInstance.getFromCache('req', url, CacheAreaKey.Profile);

    if (storedItems) {
      return Promise.resolve(storedItems as ProfilePreferences);
    };

    return HttpClient.GetInstance.get<ProfilePreferences>(url)
      .then(
        (res: AxiosResponse<ProfilePreferences>) => (
          (StorageHelper<ProfilePreferences>).GetInstance.setCacheAndReturn('req', CacheAreaKey.Profile, res?.data, url) as ProfilePreferences
        )
      );
  }

  static updatePreferences(customerNumber?: string, country?: string, payLoad?: ProfilePreferences) {
    const url = this.apiBuilder.createUrl('Preferences')
      .addRouteParam('Update')
      .addQueryParam('country', country?.toLowerCase())
      .addQueryParam('BusinessUnit', SiteConfigStore.businessUnit())
      .addQueryParam('Channel', SiteConfigStore.channel())
      .addQueryParam('customerNumber', customerNumber)
      .build();

    return HttpClient.GetInstance.post<any, ProfilePreferences>(url, payLoad)
      .then(res => {
        StorageHelper.GetInstance.debounceStorageArea('req', CacheAreaKey.Profile)
        return res;
      });
  }

  static updateProfileImage(customerNumber: string, country: string, image: string): Promise<unknown> {
    const url = this.apiBuilder.createUrl('Image')
      .addRouteParam('Update')
      .addQueryParam('Country', country)
      .addQueryParam('CustomerNumber', customerNumber)
      .addQueryParam('BusinessUnit', SiteConfigStore.businessUnit())
      .addQueryParam('Channel', SiteConfigStore.channel())
      .build();

    return HttpClient.GetInstance.put<{ "file": string }, unknown>(url, { "file": image })
      .then(res => {
        StorageHelper.GetInstance.debounceStorageArea('req', CacheAreaKey.Profile)
        return res;
      });
  }

  static getLocations(customerNumber: string, country: string): Promise<ProfileLocation> {
    const url = this.apiBuilder.createUrl('UpdateContactDetails')
      .addRouteParam('GetLocations')
      .addQueryParam('Country', country?.toLowerCase())
      .addQueryParam('BusinessUnit', SiteConfigStore.businessUnit())
      .addQueryParam('Channel', SiteConfigStore.channel())
      .addQueryParam('customerNumber', customerNumber)
      .build();

    const storedItems = (StorageHelper<ProfileLocation>).GetInstance.getFromCache('req', url, CacheAreaKey.Profile);
    if (storedItems) {
      return Promise.resolve(storedItems as ProfileLocation);
    };

    return HttpClient.GetInstance.get<ResponseFromMAHGetLocations | ResponseFromZAGetLocations>(url)
      .then(
        (res: AxiosResponse<ResponseFromMAHGetLocations | ResponseFromZAGetLocations>) => (
          (StorageHelper<ProfileLocation>).GetInstance.setCacheAndReturn('req', CacheAreaKey.Profile, LocationHelper.MapToLocations(res?.data), url) as ProfileLocation
        )
      );
  }

  static updateProfile(customerNumber?: string, country?: string, payLoad?: any) {
    const url = this.apiBuilder.createUrl('UpdateContactDetails')
      .addRouteParam('UpdateCustomerProfile')
      .addQueryParam('country', country?.toLowerCase())
      .addQueryParam('BusinessUnit', SiteConfigStore.businessUnit())
      .addQueryParam('Channel', SiteConfigStore.channel())
      .addQueryParam('customerNumber', customerNumber) // customerNumber)
      .build();

    return HttpClient.GetInstance.put<any, unknown>(url, payLoad)
      .then(res => {
        StorageHelper.GetInstance.debounceStorageArea('req', CacheAreaKey.Profile)
        return res;
      });
  }

  static getCountriesFromMenu() {
    const url = this.menuApiBuilder.createUrl('getmenucountries').build();
    return HttpClient.GetInstance.get<ProfileCountry[]>(url).then((res) => {
      return res.data.map((country) => {
        return { text: country.name, value: country.code };
      });
    });
  }

}